import React, { useState } from 'react';
import { Building, Download, Users, Loader2, CheckCircle2, XCircle, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { apiService } from '@/services/api';
import { exportOrganizationalStructureToExcel } from '@/utils/exportUtils';
import type { ApiTeam, ApiUser } from '@/services/api';

interface OrganizationalData {
  teams: ApiTeam[];
  users: ApiUser[];
}

export function OrganizationalStructureExporter() {
  const [isLoading, setIsLoading] = useState(false);
  const [lastExportData, setLastExportData] = useState<OrganizationalData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleExportStructure = async () => {
    setIsLoading(true);
    setError(null);
    setExportSuccess(false);

    try {
      // Fetch fresh data from API
      const [teams, users] = await Promise.all([
        apiService.getTeams(),
        apiService.getAllUsers()
      ]);

      // Store data for statistics display
      setLastExportData({ teams, users });

      // Generate filename with current date and time
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const filename = `structura-organizationala-${dateStr}-${timeStr}.xlsx`;

      // Export to Excel
      await exportOrganizationalStructureToExcel(teams, users, filename);
      
      setExportSuccess(true);
    } catch (err: unknown) {
      console.error('Error exporting organizational structure:', err);
      setError((err instanceof Error ? err.message : String(err)) || 'A apărut o eroare la exportarea structurii organizaționale');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadAgain = async () => {
    if (!lastExportData) return;

    try {
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const filename = `structura-organizationala-${dateStr}-${timeStr}.xlsx`;

      await exportOrganizationalStructureToExcel(
        lastExportData.teams, 
        lastExportData.users, 
        filename
      );
    } catch (err: unknown) {
      console.error('Error re-downloading structure:', err);
      setError('A apărut o eroare la descărcarea fișierului');
    }
  };

  // Calculate statistics if data is available
  const getStatistics = () => {
    if (!lastExportData) return null;

    const { teams, users } = lastExportData;
    const totalUsers = users.length;
    const totalTeams = teams.length;
    const totalWorkers = users.filter(u => u.role === 'worker').length;
    const totalSupervisors = users.filter(u => u.role === 'supervisor').length;
    const totalMonitors = users.filter(u => u.role === 'monitor').length;
    const activeTeams = teams.filter(t => t.members.length > 0).length;
    const unassignedUsers = users.filter(u => !teams.some(t => 
      t.members.some(m => m.id === u.id) || t.supervisorId === u.id || t.monitorId === u.id
    )).length;

    return {
      totalUsers,
      totalTeams,
      totalWorkers,
      totalSupervisors,
      totalMonitors,
      activeTeams,
      unassignedUsers
    };
  };

  const stats = getStatistics();

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building className="h-5 w-5" />
          Export Structură Organizațională
        </CardTitle>
        <CardDescription>
          Exportă structura organizațională completă cu toate echipele, supervisorii, monitorii și membrii în format Excel
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Information Alert */}
        <Alert>
          <BarChart3 className="h-4 w-4" />
          <AlertDescription>
            <strong>Ce include exportul:</strong> Structura ierarhică detaliată, sumarul echipelor, 
            lista supervisorilor și monitorilor, toți utilizatorii și statistici complete. 
            Fișierul Excel va conține 5 foi de calcul cu informații comprehensive.
          </AlertDescription>
        </Alert>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button 
            onClick={handleExportStructure}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isLoading ? 'Se exportă structura...' : 'Exportă Structura Organizațională'}
          </Button>

          {lastExportData && (
            <Button 
              variant="outline" 
              onClick={downloadAgain}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Descarcă din nou
            </Button>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success Message */}
        {exportSuccess && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <strong>Export realizat cu succes!</strong> Fișierul Excel cu structura organizațională a fost descărcat.
            </AlertDescription>
          </Alert>
        )}

        {/* Statistics Display */}
        {stats && (
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-gray-700">
              Statistici organizaționale din ultimul export:
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">{stats.totalUsers}</div>
                <div className="text-xs text-blue-600">Total Utilizatori</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <div className="text-2xl font-bold text-green-700">{stats.totalTeams}</div>
                <div className="text-xs text-green-600">Total Echipe</div>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <div className="text-2xl font-bold text-purple-700">{stats.totalWorkers}</div>
                <div className="text-xs text-purple-600">Muncitori</div>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                <div className="text-2xl font-bold text-orange-700">{stats.activeTeams}</div>
                <div className="text-xs text-orange-600">Echipe Active</div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{stats.totalSupervisors}</Badge>
                <span className="text-sm text-gray-600">Supervisori</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{stats.totalMonitors}</Badge>
                <span className="text-sm text-gray-600">Monitori</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={stats.unassignedUsers > 0 ? "destructive" : "secondary"}>
                  {stats.unassignedUsers}
                </Badge>
                <span className="text-sm text-gray-600">Utilizatori neasignați</span>
              </div>
            </div>

            {/* Export Details */}
            <div className="bg-gray-50 p-3 rounded-lg border">
              <h5 className="font-medium text-sm mb-2">Conținutul exportului:</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-gray-600">
                <div>• Structură organizațională ierarhică</div>
                <div>• Sumarul echipelor cu detalii</div>
                <div>• Lista supervisorilor și monitorilor</div>
                <div>• Toți utilizatorii cu statusul lor</div>
                <div>• Statistici comprehensive</div>
                <div>• Analiza rolurilor și responsabilităților</div>
              </div>
            </div>
          </div>
        )}

        {/* Features List */}
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-gray-700">Caracteristici ale exportului:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              <span>Vizualizare ierarhică cu emojii și indentare</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              <span>5 foi de calcul specializate</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              <span>Statistici automate și analize</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              <span>Identificarea utilizatorilor neasignați</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              <span>Formatare profesională pentru print</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              <span>Date în timp real din baza de date</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}